import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import api, { productImage } from "@/lib/api";
import StorefrontLayout from "@/components/StorefrontLayout";
import AIChat from "@/components/AIChat";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [trending, setTrending] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => {
      const all = r.data || [];
      setProducts(all.slice(0, 6));
      // Trending = newest 8 AI-generated items (or newest 8 overall if none)
      const ai = all.filter((p) => p.ai_generated).slice(0, 8);
      setTrending(ai.length ? ai : all.slice(0, 8));
    }).catch(() => {});
  }, []);

  return (
    <StorefrontLayout>
      {/* ─── Hero ─── */}
      <section className="rule-b">
        <div className="max-w-[1320px] mx-auto px-6 sm:px-10 pt-16 sm:pt-24 pb-16 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7 rise rise-1">
            <div className="label-mono mb-8" style={{ color: "var(--accent)" }}>DIGITAL SOLUTIONS · LAUNCH WEEK · MMXXVI</div>
            <h1 className="font-display text-[11vw] sm:text-[5.5vw] leading-[1.02] font-bold tracking-tight">
              Stop Starting<br /><span style={{ color: "var(--accent)" }}>From Scratch.</span>
            </h1>
            <p className="mt-8 text-lg leading-relaxed max-w-xl" style={{ color: "var(--ink-soft)" }}>
              Practical digital tools that help you create content, grow your business, save time, and turn ideas into action.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/shop" data-testid="hero-shop-btn" className="btn-pill">Shop Bestsellers <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/#free-starter-kit" data-testid="hero-starter-kit-btn" className="btn-ghost">Get the Free Starter Kit</Link>
            </div>
            <div className="mt-12 flex items-center gap-6 text-sm">
              <span className="badge-emerald"><Sparkles className="w-3.5 h-3.5" /> Instant download</span>
              <span className="badge-blue">Secure Stripe checkout</span>
            </div>
          </div>

          <div className="lg:col-span-5 rise rise-2">
            <div className="aspect-[4/5] overflow-hidden relative bx">
              {products[0] && (
                <img src={productImage(products[0])} alt={products[0].title} className="w-full h-full object-cover" />
              )}
              <div className="absolute top-4 left-4">
                <span className="badge-emerald"><Sparkles className="w-3.5 h-3.5" /> Instant download</span>
              </div>
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                <div className="px-3 py-1.5 rounded-full text-xs font-mono-ui glass">
                  Featured pick
                </div>
                {products[0] && (
                  <Link to={`/product/${products[0].id}`} className="px-3 py-1.5 rounded-full text-xs font-mono-ui" style={{ background: "var(--ink)", color: "#ffffff" }}>
                    View →
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Trending marquee ─── */}
      <TrendingStrip items={trending} />

      {/* ─── Problem Selector · What are you trying to accomplish? ─── */}
      <ProblemSelector />

      {/* ─── Featured Categories ─── */}
      <CategoryGrid />

      {/* ─── Intent search + Trending strip ─── */}
      <IntentSearchSection />

      {/* ─── Featured grid ─── */}
      <section>
        <div className="max-w-[1320px] mx-auto px-6 sm:px-10 pt-20">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="label-mono mb-2">/ BEST SELLERS</div>
              <h2 className="font-display text-5xl sm:text-6xl font-bold tracking-tight">Creator favorites.</h2>
            </div>
            <Link to="/shop" data-testid="see-all" className="text-sm font-medium underline underline-offset-4 hover:opacity-60">See everything →</Link>
          </div>
        </div>
        <div className="max-w-[1320px] mx-auto px-6 sm:px-10 pb-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((p, idx) => (
            <Link
              key={p.id}
              to={`/product/${p.id}`}
              data-testid={`product-card-${p.id}`}
              className={`product-card group block rise rise-${(idx % 4) + 1}`}
            >
              <div className="relative aspect-[4/5] overflow-hidden bx">
                <img src={productImage(p)} alt={p.title} className="w-full h-full object-cover transition duration-700 group-hover:scale-[1.04]" />
                <div className="product-overlay" />
                <div className="absolute top-3 left-3">
                  <span className="badge-emerald"><Sparkles className="w-3 h-3" /> Instant</span>
                </div>
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between opacity-0 group-hover:opacity-100 transition">
                  <span className="px-2.5 py-1 text-xs font-mono-ui rounded-full glass">{p.category}</span>
                  <span className="px-2.5 py-1 text-xs font-mono-ui rounded-full" style={{ background: "#ffffff" }}>View →</span>
                </div>
              </div>
              <div className="mt-4 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display text-xl font-semibold truncate">{p.title}</div>
                  <div className="label-mono mt-1">{p.category}{p.ai_generated && " · AI CURATED"}</div>
                </div>
                <div className="font-mono-ui text-base mt-1 font-semibold">${p.price.toFixed(2)}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ─── Why Choose section ─── */}
      <section className="mt-24" style={{ background: "#ffffff" }}>
        <div className="max-w-[1320px] mx-auto px-6 sm:px-10 py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="label-mono mb-4">/ WHY MERIDIANPRESS</div>
            <h2 className="font-display text-5xl font-bold tracking-tight">Built for creators<br />who ship.</h2>
          </div>
          <div className="lg:col-span-7 text-lg leading-relaxed" style={{ color: "var(--ink-soft)" }}>
            <p>Every product is designed to save you hours and make you money — not fill your downloads folder. Templates that actually work in Notion. Prompts that get results in ChatGPT. Planners you&rsquo;ll open every day.</p>
            <p className="mt-6">Instant download the moment you pay. Lifetime access to every update. Commercial-use license included on eligible products. And a 30-day money-back promise on everything — no questions asked.</p>
            <div className="mt-8 flex flex-wrap gap-2">
              <span className="badge-emerald">Instant download</span>
              <span className="badge-blue">Lifetime access</span>
              <span className="badge-purple">Commercial-use ready</span>
              <span className="badge-emerald">30-day money-back</span>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Newsletter ─── */}
      <NewsletterSection />

      <AIChat />
    </StorefrontLayout>
  );
}


// ─── Newsletter with real email capture → coupon ───
function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [coupon, setCoupon] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || status === "busy") return;
    setStatus("busy");
    try {
      const r = await api.post("/subscribe", { email, source: "newsletter" });
      if (r.data?.coupon) { setCoupon(r.data.coupon); setStatus("done"); } else { setStatus("err"); }
    } catch { setStatus("err"); }
  };

  return (
    <section id="free-starter-kit" className="rule-t" style={{ background: "var(--ink)", color: "#ffffff" }}>
      <div className="max-w-[1320px] mx-auto px-6 sm:px-10 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="label-mono mb-3" style={{ color: "var(--accent-soft)" }}>/ FREE STARTER KIT · 50 AI PROMPTS</div>
          <h2 className="font-display text-5xl font-bold tracking-tight">Get 30 days of content prompts — free.</h2>
          <p className="mt-4 text-lg opacity-70 max-w-md">A hand-picked PDF of 50 AI prompts across hooks, frameworks, captions, business, and productivity. Drop your email — we&rsquo;ll send it right over, plus a welcome discount.</p>
        </div>
        {status !== "done" ? (
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="newsletter-email"
              type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourdomain.com"
              className="flex-1 px-5 py-4 rounded-full text-base outline-none"
              style={{ background: "rgba(255,255,255,0.08)", color: "#ffffff", border: "1px solid rgba(255,255,255,0.15)" }}
            />
            <button data-testid="newsletter-submit" type="submit" disabled={status === "busy"} className="px-6 py-4 rounded-full font-semibold text-sm text-white" style={{ background: "var(--accent)" }}>
              {status === "busy" ? "…" : "Get the free PDF →"}
            </button>
          </form>
        ) : (
          <div data-testid="newsletter-code" className="rounded-2xl p-6 text-center" style={{ background: "rgba(255,255,255,0.06)" }}>
            <div className="label-mono opacity-70" style={{ color: "#ffffff" }}>YOUR STARTER KIT IS ON THE WAY</div>
            <div className="wordmark text-2xl mt-2" style={{ color: "var(--accent-soft)" }}>Check your inbox in ~1 min</div>
            <div className="text-sm mt-2 opacity-80">50 AI prompts (PDF) — plus code <strong>{coupon?.code}</strong> for {coupon?.discount_pct}% off.</div>
            <a href="/api/starter-kit/download" data-testid="starter-kit-inline-download" className="inline-block mt-4 px-4 py-2 rounded-full text-sm font-mono-ui" style={{ background: "var(--accent)", color: "#ffffff" }}>Download PDF now →</a>
          </div>
        )}
        {status === "err" && <div className="text-sm" style={{ color: "var(--accent-soft)" }}>Something went wrong. Try again.</div>}
      </div>
    </section>
  );
}

// ─── Intent search: describe what you want, AI finds it ───
function IntentSearchSection() {
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [reasoning, setReasoning] = useState("");

  const run = async (e) => {
    e?.preventDefault();
    if (!query.trim() || busy) return;
    setBusy(true);
    try {
      const r = await api.post("/search/intent", { query });
      setResults(r.data.results || []);
      setReasoning(r.data.reasoning || "");
    } catch (err) {
      setResults([]);
      setReasoning("Search unavailable — try again in a moment.");
    } finally { setBusy(false); }
  };

  return (
    <section className="rule-t rule-b" style={{ background: "#ffffff" }}>
      <div className="max-w-[1320px] mx-auto px-6 sm:px-10 py-16">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="label-mono mb-2" style={{ color: "var(--accent)" }}>/ AI-POWERED SEARCH</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
              Describe what you need. We&rsquo;ll find it.
            </h2>
          </div>
          <span className="label-mono">POWERED BY AI</span>
        </div>
        <form onSubmit={run} className="flex flex-col sm:flex-row gap-3 max-w-3xl">
          <input
            data-testid="intent-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='e.g. "AI prompts for LinkedIn posts" or "a Notion template for weekly reviews"'
            className="flex-1 px-5 py-4 rounded-full text-base outline-none"
            style={{ background: "var(--slate-50)", border: "1px solid var(--slate-200)" }}
          />
          <button data-testid="intent-search-submit" type="submit" disabled={busy} className="btn-pill">
            {busy ? "Thinking…" : "Find it →"}
          </button>
        </form>

        {results && (
          <div className="mt-8" data-testid="intent-search-results">
            {reasoning && <p className="text-sm mb-4 italic" style={{ color: "var(--ink-soft)" }}>{reasoning}</p>}
            {results.length === 0 ? (
              <p className="text-sm" style={{ color: "var(--ink-soft)" }}>Nothing matched. Try broader phrasing.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {results.map((p) => (
                  <Link key={p.id} to={`/product/${p.id}`} data-testid={`intent-match-${p.id}`} className="product-card group block">
                    <div className="relative aspect-[4/5] overflow-hidden bx">
                      <img src={productImage(p)} alt={p.title} className="w-full h-full object-cover transition duration-500 group-hover:scale-[1.04]" />
                    </div>
                    <div className="mt-3 flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-display text-base font-semibold truncate">{p.title}</div>
                        <div className="label-mono mt-0.5">{p.category}</div>
                      </div>
                      <div className="font-mono-ui text-sm font-semibold">${p.price.toFixed(2)}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Problem Selector: 4 outcome cards → routes to matching collection ───
const OUTCOMES = [
  { label: "CREATE", tag: "Content / AI",              tint: "var(--accent)", icon: "◆", href: "/shop?category=Prompt%20Packs" },
  { label: "GROW",   tag: "Marketing / Sales",         tint: "var(--purple)", icon: "▲", href: "/shop?audience=creators" },
  { label: "ORGANIZE", tag: "Productivity / Workflows", tint: "var(--leaf)",  icon: "◇", href: "/shop?category=Templates" },
  { label: "PROFIT", tag: "Business / Monetization",   tint: "var(--accent)", icon: "✦", href: "/shop?audience=small-business" },
];

function ProblemSelector() {
  return (
    <section className="rule-t" data-testid="problem-selector">
      <div className="max-w-[1320px] mx-auto px-6 sm:px-10 pt-20">
        <div className="mb-10">
          <div className="label-mono mb-2" style={{ color: "var(--accent)" }}>/ START HERE</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">What are you trying to accomplish?</h2>
          <p className="mt-3 text-base" style={{ color: "var(--ink-soft)" }}>Not sure what you need? Pick an outcome and we&rsquo;ll take you straight to the right tools.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {OUTCOMES.map((o, i) => (
            <Link
              key={o.label}
              to={o.href}
              data-testid={`outcome-card-${o.label.toLowerCase()}`}
              className={`bx p-6 md:p-8 flex flex-col justify-between items-start rise rise-${(i % 4) + 1} transition-all duration-300 hover:-translate-y-1`}
              style={{ minHeight: 164, boxShadow: "var(--shadow-sm)" }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "var(--shadow-lg)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "var(--shadow-sm)"; }}
            >
              <div className="w-11 h-11 rounded-full flex items-center justify-center text-xl font-bold text-white"
                   style={{ background: o.tint }}>
                {o.icon}
              </div>
              <div className="mt-6">
                <div className="font-display text-lg md:text-2xl font-bold tracking-tight">{o.label}</div>
                <div className="label-mono mt-1" style={{ color: "var(--ink-soft)" }}>{o.tag}</div>
                <div className="label-mono mt-3" style={{ color: "var(--accent)" }}>Explore →</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Featured Categories grid — 8 tappable cards, hover-lift animations ───
const CATEGORIES = [
  { label: "AI Prompts",       query: "AI Skills Guides", tint: "var(--accent)",      icon: "◆" },
  { label: "Templates",        query: "Templates",         tint: "var(--purple)",      icon: "▲" },
  { label: "Prompt Packs",     query: "Prompt Packs",      tint: "var(--accent)",      icon: "✦" },
  { label: "Digital Downloads",query: "Digital",           tint: "var(--leaf)",        icon: "↓" },
  { label: "Health Guides",    query: "Health Guides",     tint: "var(--leaf)",        icon: "♡" },
  { label: "Life Guides",      query: "Life Guides",       tint: "var(--purple)",      icon: "◇" },
  { label: "Presets",          query: "Presets",           tint: "var(--accent)",      icon: "◐" },
  { label: "Wallpapers",       query: "Wallpapers",        tint: "var(--purple)",      icon: "◈" },
];

function CategoryGrid() {
  return (
    <section>
      <div className="max-w-[1320px] mx-auto px-6 sm:px-10 pt-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="label-mono mb-2">/ SHOP BY CATEGORY</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Everything a creator needs.</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {CATEGORIES.map((c, i) => (
            <Link
              key={c.label}
              to={`/shop?category=${encodeURIComponent(c.query)}`}
              data-testid={`category-card-${c.query.toLowerCase().replace(/\s+/g, '-')}`}
              className={`bx p-6 md:p-8 flex flex-col justify-between items-start rise rise-${(i % 4) + 1} transition-all duration-300 hover:-translate-y-1`}
              style={{ minHeight: 148, boxShadow: "var(--shadow-sm)" }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "var(--shadow-lg)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "var(--shadow-sm)"; }}
            >
              <div className="w-11 h-11 rounded-full flex items-center justify-center text-xl font-bold text-white"
                   style={{ background: c.tint }}>
                {c.icon}
              </div>
              <div className="mt-6">
                <div className="font-display text-lg md:text-xl font-semibold">{c.label}</div>
                <div className="label-mono mt-1" style={{ color: "var(--accent)" }}>Shop →</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Trending Now strip ───
function TrendingStrip({ items }) {
  if (!items || items.length === 0) return null;
  const doubled = [...items, ...items];
  return (
    <section className="trending-strip rule-t rule-b">
      <div className="max-w-[1320px] mx-auto px-6 sm:px-10 py-4 flex items-center gap-6 overflow-hidden">
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="ai-dot" />
          <span className="label-mono" style={{ color: "var(--accent)" }}>TRENDING NOW</span>
        </div>
        <div className="marquee-track font-mono-ui text-sm">
          {doubled.map((p, i) => (
            <Link key={`${p.id}-${i}`} to={`/product/${p.id}`} className="hover:opacity-60 transition">
              <span style={{ color: "var(--ink-soft)" }}>{p.category}</span>
              <span className="mx-2">·</span>
              <span className="font-medium" style={{ color: "var(--ink)" }}>{p.title}</span>
              <span className="mx-2" style={{ color: "var(--accent)" }}>${p.price.toFixed(2)}</span>
              <span className="mx-6" style={{ color: "var(--ink-mute)" }}>◆</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
